import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import SlideOver from "../../Modal/SlideOver";
import { PencilIcon } from "@heroicons/react/outline";
import { currencyFormat } from "../../../../Utils";

const CashbookTransactionDetailModal = ({
  open,
  onClose,
  onEdit,
  transaction,
}) => {
  const { t, i18n } = useTranslation();
  const [isNoteExpanded, setIsNoteExpanded] = useState(false);

  if (!transaction) return null;

  const isIncome = transaction.income > 0;
  const amount = isIncome ? transaction.income : transaction.expense;
  const amountColorClass = isIncome ? "text-green-600" : "text-red-600";
  const badgeColorClass = isIncome
    ? "bg-green-100 text-green-800"
    : "bg-red-100 text-red-800";
  const typeLabel = isIncome
    ? t("cashbook.type.sale")
    : t("cashbook.type.expense");
  const prefix = isIncome ? "+" : "-";

  const noteLength = transaction.note?.length || 0;
  const isLongNote = noteLength > 100;

  return (
    <SlideOver
      open={open}
      onClose={onClose}
      title="cashbook.detail.title"
      actionButton={
        onEdit && (
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600 transition-colors"
            title={t("common.edit")}
            onClick={() => onEdit(transaction)}
          >
            <PencilIcon className="h-5 w-5" />
          </button>
        )
      }
    >
      <div className="space-y-6 mb-6">
        {/* Header Summary */}
        <div className="bg-white px-4 py-5 shadow sm:rounded-lg sm:p-6 border border-gray-100">
          {/* Amount & Type Badge */}
          <div className="flex justify-between items-center mb-4">
            <div>
              <p className="text-sm font-medium text-gray-500">
                {t("cashbook.detail.breakdown.amount")}
              </p>
              <h3 className={`mt-1 text-3xl font-semibold ${amountColorClass}`}>
                {prefix}
                {currencyFormat(amount)}
              </h3>
            </div>
            <span
              className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold leading-5 ${badgeColorClass}`}
            >
              {typeLabel}
            </span>
          </div>

          <dl className="grid grid-cols-1 gap-x-4 gap-y-4 sm:grid-cols-2 mt-4 pt-4 border-t border-gray-200">
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("cashbook.dashboard.transaction.field.date")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {dayjs(transaction.createdAt)
                  .locale(i18n.language)
                  .format("DD MMM YYYY, HH:mm")}
              </dd>
            </div>
            <div className="sm:col-span-1">
              <dt className="text-sm font-medium text-gray-500">
                {t("cashbook.detail.fields.transactionId")}
              </dt>
              <dd
                className="mt-1 text-sm text-gray-900 font-mono uppercase truncate"
                title={transaction.id}
              >
                {transaction.id.slice(0, 5)}
              </dd>
            </div>

            <div className="sm:col-span-2">
              <dt className="text-sm font-medium text-gray-500">
                {t("cashbook.dashboard.transaction.field.note")}
              </dt>
              <dd className="mt-1 text-sm text-gray-900">
                {transaction.note ? (
                  <>
                    <p
                      className={`whitespace-pre-wrap ${
                        !isNoteExpanded && isLongNote ? "line-clamp-2" : ""
                      }`}
                    >
                      {transaction.note}
                    </p>
                    {isLongNote && (
                      <button
                        type="button"
                        className="block mt-1 text-xs text-blue-600 hover:text-blue-500 font-medium text-left"
                        onClick={() => setIsNoteExpanded(!isNoteExpanded)}
                      >
                        {isNoteExpanded ? t("Show less") : t("Read more")}
                      </button>
                    )}
                  </>
                ) : (
                  <span className="text-gray-400 italic">
                    {t("cashbook.dashboard.transaction.noNote")}
                  </span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </SlideOver>
  );
};

export default CashbookTransactionDetailModal;
